import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { ProgressSliderWithToggle } from "./ProgressSliderWithToggle";

interface TodoItem {
  id: string;
  feature: string;
  description?: string;
  frontendPercent: number;
  backendPercent: number;
  infrastructurePercent: number;
  statusPercent: number;
  categoryId: string;
}

interface TodoEditModalProps {
  item: TodoItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updates: Partial<TodoItem>) => Promise<void>;
}

function calculateStatusPercent(fe: number, be: number, infra: number): number {
  const values = [fe, be, infra].filter((v) => v !== -1);
  if (values.length === 0) return 0;
  return Math.round(values.reduce((sum, v) => sum + v, 0) / values.length);
}

export function TodoEditModal({ item, isOpen, onClose, onSave }: TodoEditModalProps) {
  const [formData, setFormData] = useState<Partial<TodoItem>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Reset form when item changes
  useEffect(() => {
    if (item) {
      setFormData({
        feature: item.feature,
        description: item.description || "",
        frontendPercent: item.frontendPercent,
        backendPercent: item.backendPercent,
        infrastructurePercent: item.infrastructurePercent,
      });
    }
  }, [item]);

  if (!item) return null;

  const statusPercent = calculateStatusPercent(
    formData.frontendPercent ?? 0,
    formData.backendPercent ?? 0,
    formData.infrastructurePercent ?? 0
  );

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({
        ...formData,
        statusPercent,
      });
      onClose();
    } catch (error) {
      console.error("Failed to save:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] h-[90vh] max-w-none flex flex-col p-0">
        {/* Header */}
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="text-xl">Edit Todo Item</DialogTitle>
          <div className="mt-2">
            <Label htmlFor="feature-name" className="text-sm text-muted-foreground">
              Feature Name
            </Label>
            <Input
              id="feature-name"
              value={formData.feature || ""}
              onChange={(e) => setFormData({ ...formData, feature: e.target.value })}
              className="mt-1 text-lg font-semibold"
              placeholder="Enter feature name..."
            />
          </div>
        </DialogHeader>

        {/* Body - 2 Column Layout */}
        <div className="flex-1 grid grid-cols-[350px_1fr] gap-6 overflow-hidden px-6 py-4 min-h-0">
          {/* Left Panel - Metadata */}
          <div className="space-y-6 overflow-y-auto pr-4 border-r min-h-0">
            {/* Frontend Progress */}
            <ProgressSliderWithToggle
              label="Frontend"
              value={formData.frontendPercent ?? 0}
              onChange={(value) => setFormData({ ...formData, frontendPercent: value })}
            />

            {/* Backend Progress */}
            <ProgressSliderWithToggle
              label="Backend"
              value={formData.backendPercent ?? 0}
              onChange={(value) => setFormData({ ...formData, backendPercent: value })}
            />

            {/* Infrastructure Progress */}
            <ProgressSliderWithToggle
              label="Infrastructure"
              value={formData.infrastructurePercent ?? 0}
              onChange={(value) => setFormData({ ...formData, infrastructurePercent: value })}
            />

            {/* Overall Status (Read-only) */}
            <div className="pt-4 border-t space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">Overall Status</Label>
              <div className="text-4xl font-bold tabular-nums">{statusPercent}%</div>
              <Progress value={statusPercent} className="h-3" />
              <p className="text-xs text-muted-foreground">
                Auto-calculated from non-N/A dimensions
              </p>
            </div>
          </div>

          {/* Right Panel - Markdown Editor */}
          <div className="flex flex-col overflow-hidden min-h-0">
            <div className="flex items-center justify-between mb-3 flex-shrink-0">
              <Label className="text-sm font-medium">Description (Markdown)</Label>
            </div>

            {/* Markdown Editor */}
            <Textarea
              value={formData.description || ""}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add notes here..."
              className="flex-1 font-mono text-sm resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="border-t px-6 py-4">
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
